import { createSlice } from "@reduxjs/toolkit"
import { resolveMediaAssetIdFromUploadMediaRow } from "../../lib/utils/mediaAssetId"

export const FILE_STATUS = {
  UPLOADING: "uploading",
  PROCESSING: "processing",
  CATEGORIZING: "categorizing",
  READY: "ready",
  FAILED: "failed",
}

const inferFileType = (name) => {
  if (!name || typeof name !== "string") return "image"
  return /\.(mp4|mov|qt|webm|avi)$/i.test(name) ? "video" : "image"
}

const initialState = {
  phase: "idle",
  totalAssets: 0,
  processedCount: 0,
  uploadProgress: 0,
  failedCount: 0,
  files: [],
  isPanelExpanded: false,
  isDismissed: false,
  clientId: null,
  preUploadCount: 0,
  /** Hide bottom floating panel during HTTP upload when library was empty (full-page transition handles UX). */
  hideFloatingForFirstTimeUpload: false,
}

const assetUploadSlice = createSlice({
  name: "assetUpload",
  initialState,
  reducers: {
    startUpload: (state, action) => {
      const { totalAssets, clientId, fileNames = [], previewUrls = [], preUploadCount = 0 } = action.payload
      state.phase = "uploading"
      state.hideFloatingForFirstTimeUpload = preUploadCount === 0
      state.totalAssets = totalAssets
      state.clientId = clientId
      state.preUploadCount = preUploadCount
      state.uploadProgress = 0
      state.processedCount = 0
      state.failedCount = 0
      state.isDismissed = false
      state.isPanelExpanded = false
      const base = Date.now()
      state.files = fileNames.map((name, i) => ({
        id: `f-${base}-${i}`,
        name,
        type: inferFileType(name),
        status: FILE_STATUS.UPLOADING,
        previewUrl: previewUrls[i] || null,
        mediaAssetId: null,
      }))
    },
    setUploadProgress: (state, action) => {
      state.uploadProgress = Math.min(100, Math.max(0, action.payload ?? 0))
    },
    /** After multipart upload succeeds: attach media asset ids and move to AI processing phase */
    finishHttpUpload: (state, action) => {
      const { apiResult, preUploadCount } = action.payload
      if (typeof preUploadCount === "number") state.preUploadCount = preUploadCount
      state.hideFloatingForFirstTimeUpload = false
      state.phase = "processing"
      state.uploadProgress = 100
      const medias = apiResult?.medias || []
      state.files = state.files.map((f, i) => {
        const media = medias[i]
        const mid = resolveMediaAssetIdFromUploadMediaRow(media)
        return {
          ...f,
          mediaAssetId: mid,
          status: FILE_STATUS.PROCESSING,
        }
      })
      state.processedCount = 0
    },
    applyAnalysisStatusMap: (state, action) => {
      const statusByAssetId = action.payload?.statusByAssetId || {}
      const mapStatusToFile = (apiStatus) => {
        const s = String(apiStatus || "").toUpperCase()
        if (s === "FAILED" || s === "ERROR") return FILE_STATUS.FAILED
        if (
          s === "ANALYZED" ||
          s === "ANALYSED" ||
          s === "COMPLETE" ||
          s === "DONE" ||
          s === "SUCCESS" ||
          s === "READY"
        ) {
          return FILE_STATUS.READY
        }
        if (s === "CATEGORIZING" || s === "CATEGORISING") return FILE_STATUS.CATEGORIZING
        if (s === "ANALYZING" || s === "PENDING" || s === "IN_PROGRESS" || s === "PROCESSING") {
          return FILE_STATUS.PROCESSING
        }
        return FILE_STATUS.PROCESSING
      }

      state.files = state.files.map((f) => {
        if (f.status === FILE_STATUS.FAILED) return f
        if (f.mediaAssetId == null) return f
        const id = f.mediaAssetId
        const raw =
          statusByAssetId[id] ?? statusByAssetId[String(id)] ?? statusByAssetId[Number(id)]
        if (raw == null) return f
        return { ...f, status: mapStatusToFile(raw) }
      })

      const done = state.files.filter(
        (f) => f.status === FILE_STATUS.READY || f.status === FILE_STATUS.FAILED,
      ).length
      state.processedCount = done
      state.failedCount = state.files.filter((f) => f.status === FILE_STATUS.FAILED).length

      if (state.totalAssets > 0 && done >= state.totalAssets) {
        state.phase = "complete"
      }
    },
    setProcessedCount: (state, action) => {
      state.processedCount = Math.min(state.totalAssets, Math.max(0, action.payload ?? 0))
    },
    processingComplete: (state) => {
      state.phase = "complete"
      state.processedCount = state.totalAssets
      state.files = state.files.map((f) =>
        f.status === FILE_STATUS.FAILED ? f : { ...f, status: FILE_STATUS.READY },
      )
    },
    setUploadFailed: (state, action) => {
      const n = action.payload?.failedCount ?? state.totalAssets
      state.hideFloatingForFirstTimeUpload = false
      state.phase = "failed"
      state.failedCount = n
      state.files = state.files.map((f) => ({ ...f, status: FILE_STATUS.FAILED }))
    },
    togglePanel: (state) => {
      state.isPanelExpanded = !state.isPanelExpanded
    },
    dismissPanel: (state) => {
      state.isDismissed = true
    },
    resetUploadState: () => ({
      ...initialState,
      files: [],
    }),
  },
})

export const {
  startUpload,
  setUploadProgress,
  finishHttpUpload,
  applyAnalysisStatusMap,
  setProcessedCount,
  processingComplete,
  setUploadFailed,
  togglePanel,
  dismissPanel,
  resetUploadState,
} = assetUploadSlice.actions

export default assetUploadSlice.reducer
